import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useMergeValue } from '../../../hooks/useMergeValue.js';
import { findValidOptionKey } from './getValidOptionKey.js';
import { getValidValue, flatChildren, isEmptyValue } from './selectHelper.js';
import { Tab, Enter } from '../../../utils/keycode.js';
import { isArray, isObject } from '../../../utils/is.js';

const THRESHOLD_TOKEN_SEPARATOR_TRIGGER = 100;
const useSelect = props => {
  const {
    children,
    options,
    filterOption = true,
    mode = 'single',
    labelInValue,
    triggerProps,
    defaultActiveFirstOption = true,
    onInputValueChange,
    onVisibleChange,
    onSearch,
    onChange,
    showSearch,
    tokenSeparators,
    onFocus,
    onBlur,
    onClear
  } = props;

  // is multiple mode
  const isMultipleMode = mode === 'multiple';
  // statevalue
  const [stateValue, setValue] = useState(getValidValue(props.defaultValue, isMultipleMode, options, children));
  // fake value to display as input text
  // actual value to return
  const value = 'value' in props ? getValidValue(props.value, isMultipleMode, options, children) : stateValue;
  // input value
  const [inputValue, setInputValue, stateInputValue] = useMergeValue('', {
    value: 'inputValue' in props ? props.inputValue || '' : undefined
  });
  const triggerPropsObj = triggerProps && 'popupVisible' in triggerProps ? triggerProps.popupVisible : undefined;
  const [popupVisible, setPopupVisible] = useMergeValue(false, {
    defaultValue: props.defaultPopupVisible,
    value: 'popupVisible' in props ? props.popupVisible : triggerPropsObj
  });

  // hover  option  value
  const [valueActive, setValueActive] = useState(isArray(value) ? value[0] : value);

  // ref
  const refWrapper = useRef(null);
  const refTrigger = useRef(null);
  const refSelectView = useRef(null);
  const refValueMap = useRef([]);
  const refKeyboardArrowDirection = useRef(null);
  const refOnInputChangeCallbackValue = useRef(inputValue);
  const refOnInputChangeCallbackReason = useRef(null);
  const refTSLastSeparateTriggered = useRef(0);
  const {
    childrenList,
    optionInfoMap,
    optionValueList,
    optionIndexListForArrowKey,
    hasOptGroup
  } = useMemo(() => {
    return flatChildren({
      children,
      options,
      filterOption
    }, {
      inputValue
    });
  }, [children, options, filterOption, inputValue]);
  const isNoOptionSelected = isEmptyValue(value, isMultipleMode);
  const valueActiveDefault = defaultActiveFirstOption ? optionValueList[optionIndexListForArrowKey[0]] : undefined;
  const scrollIntoView = useCallback(optionValue => {
    const activeOption = optionInfoMap.get(optionValue);
    if (refWrapper.current && activeOption && activeOption.child.props) {
      refWrapper.current.scrollTo({
        key: activeOption.child.props._key
      });
    }
  }, [optionInfoMap]);
  const tryUpdateInputValue = useCallback((value, reason) => {
    if (value !== refOnInputChangeCallbackValue.current) {
      setInputValue(value);
      refOnInputChangeCallbackValue.current = value;
      refOnInputChangeCallbackReason.current = reason;
      onInputValueChange && onInputValueChange(value, reason);
    }
  }, [onInputValueChange, setInputValue]);

  // popupVisible, onVisibleChange
  const tryUpdatePopupVisible = useCallback(value => {
    if (popupVisible !== value) {
      setPopupVisible(value);
      onVisibleChange && onVisibleChange(value);
      triggerProps && triggerProps.onVisibleChange && triggerProps.onVisibleChange(value);
    }
  }, [popupVisible, onVisibleChange, setPopupVisible, triggerProps]);

  // value
  useEffect(() => {
    if (isMultipleMode) {
      if (!Array.isArray(value)) {
        setValue(value === undefined ? [] : [`${value}`]);
      }
    } else if (Array.isArray(value)) {
      setValue(value.length === 0 ? undefined : value[0]);
    }
  }, [isMultipleMode, value]);
  useEffect(() => {
    if (popupVisible) {
      // hover Option
      const firstValue = isArray(value) ? value[0] : value;
      const nextValueActive = !isNoOptionSelected && optionInfoMap.has(firstValue) ? firstValue : valueActiveDefault;
      setValueActive(nextValueActive);
      // scrollIntoView
      setTimeout(() => scrollIntoView(nextValueActive));
    } else {
      tryUpdateInputValue('', 'optionListHide');
    }
  }, [isNoOptionSelected, optionInfoMap, popupVisible, scrollIntoView, tryUpdateInputValue, value, valueActiveDefault]);
  useEffect(() => {
    if (refKeyboardArrowDirection.current === 'up' || refKeyboardArrowDirection.current === 'down') {
      scrollIntoView(valueActive);
      refKeyboardArrowDirection.current = 'none';
    }
  }, [scrollIntoView, valueActive]);

  // active
  useEffect(() => {
    setValueActive(valueActiveDefault);
  }, [valueActiveDefault]);

  // refValueMap
  useEffect(() => {
    refValueMap.current = refValueMap.current.filter(x => {
      return isMultipleMode ? isArray(value) && value.indexOf(x.value) > -1 : x.value === value;
    });
  }, [value, isMultipleMode]);
  useEffect(() => {
    const {
      current: reason
    } = refOnInputChangeCallbackReason;
    if (stateInputValue === inputValue && (reason === 'manual' || reason === 'optionListHide')) {
      onSearch && onSearch(inputValue, reason);
    }
  }, [inputValue, onSearch, stateInputValue]);
  const getOptionInfoByValue = useCallback(value => {
    const option = optionInfoMap.get(value);
    if (option) {
      const index = refValueMap.current.findIndex(item => item.value === value);
      if (index > -1) {
        refValueMap.current.splice(index, 1, {
          value,
          option
        });
      } else {
        refValueMap.current.push({
          value,
          option
        });
      }
      return option;
    }
    const item = refValueMap.current.find(x => x.value === value);
    return item && item.option;
  }, [optionInfoMap]);

  // active option
  const getValueActive = direction => {
    if (!optionIndexListForArrowKey.length) {
      return undefined;
    }
    if (valueActive === undefined || !optionInfoMap.has(valueActive)) {
      return optionValueList[optionIndexListForArrowKey[0]];
    }
    const activeOption = optionInfoMap.get(valueActive);
    const activeIndex = activeOption._index;
    const _index = optionIndexListForArrowKey.indexOf(activeIndex);
    const _length = optionIndexListForArrowKey.length;
    return optionValueList[optionIndexListForArrowKey[((direction === 'up' ? _index - 1 : _index + 1) + _length) % _length]];
  };

  // Object should be returned when labelInValue is true
  const getValueAndOptionForCallback = useCallback((stateValue, isEmpty = isEmptyValue(stateValue, isMultipleMode)) => {
    let value = stateValue;
    const checkIsArray = Array.isArray(stateValue) ? stateValue.map(getOptionInfoByValue) : getOptionInfoByValue(stateValue);
    const option = stateValue === undefined ? undefined : checkIsArray;
    if (labelInValue && !isEmpty) {
      const getOptionLabel = (optionValue, optionInfo) => {
        if (optionInfo) {
          return optionInfo.children;
        }

        // https://github.com/arco-design/arco-design/issues/442
        // Make sure parameter value has valid label if props.value is already set
        const defVal = 'defaultValue' in props ? props.defaultValue : null;
        const propValue = 'value' in props ? props.value : defVal;

        // Multiple mode
        if (Array.isArray(propValue)) {
          for (const item of propValue) {
            if (isObject(item) && item.value === optionValue) {
              return item.label;
            }
          }
        }
        // Single mode
        else if (isObject(propValue) && propValue.value === optionValue) {
          return propValue.label;
        }
        return optionValue;
      };
      if (Array.isArray(stateValue)) {
        value = stateValue.map((optionValue, index) => ({
          value: optionValue,
          label: getOptionLabel(optionValue, option[index])
        }));
      } else {
        value = {
          value: stateValue,
          label: getOptionLabel(stateValue, option)
        };
      }
    }
    return {
      option,
      value
    };
  }, [isMultipleMode, getOptionInfoByValue, labelInValue, props]);
  const getValueAndLabelForCallback = useCallback((value, options) => {
    if (!isArray(value) && !isMultipleMode) {
      const key = findValidOptionKey(value, options);
      const opt = options.get(key);
      return {
        value: opt.value,
        label: opt.label
      };
    }
    const values = [];
    const labels = [];
    value?.filter(val => val !== '' && val !== null).map(val => {
      const key = findValidOptionKey(val, options);
      const opt = options.get(key);
      values.push(opt.value);
      labels.push(opt.label);
    });
    return {
      value: values,
      label: labels
    };
  }, [isMultipleMode]);
  const tryUpdateSelectValue = useCallback(value => {
    if (!value || typeof value === undefined) {
      setValue('');
      onChange && onChange('');
      return false;
    }
    const {
      value: nextValue,
      label: nextLabel
    } = getValueAndLabelForCallback(value, optionInfoMap);
    setValue(nextLabel);
    if (onChange) {
      onChange(nextValue);
    }
  }, [getValueAndLabelForCallback, onChange, optionInfoMap]);
  const checkOption = useCallback(valueToAdd => {
    const newValue = value.concat(valueToAdd);
    tryUpdateSelectValue(newValue);
  }, [tryUpdateSelectValue, value]);
  const uncheckOption = useCallback(valueToRemove => {
    const newValue = value.filter(v => v !== valueToRemove);
    tryUpdateSelectValue(newValue);
  }, [value, tryUpdateSelectValue]);
  const handleOptionClick = useCallback((optionValue, disabled, label) => {
    if (disabled) {
      return;
    }
    if (isMultipleMode) {
      if (value.indexOf(optionValue) === -1) {
        checkOption(label);
      } else {
        uncheckOption(label);
      }
      if (!isObject(showSearch) || !showSearch.retainInputValueWhileSelect) {
        tryUpdateInputValue('', 'optionChecked');
      }
    } else {
      if (optionValue !== value) {
        tryUpdateSelectValue(label);
      }
      setTimeout(() => {
        tryUpdatePopupVisible(false);
      });
    }
  }, [checkOption, isMultipleMode, showSearch, tryUpdateInputValue, tryUpdatePopupVisible, tryUpdateSelectValue, uncheckOption, value]);
  const hotkeyHandler = event => {
    switch (event.code) {
      case 'Enter':
        if (popupVisible) {
          const option = optionInfoMap.get(valueActive);
          option && handleOptionClick(valueActive, option.disabled, option.label);
        } else {
          tryUpdatePopupVisible(!popupVisible);
        }
        break;
      case 'ArrowDown':
        if (popupVisible) {
          refKeyboardArrowDirection.current = 'down';
          setValueActive(getValueActive('down'));
          return false;
        }
        break;
      case 'ArrowUp':
        if (popupVisible) {
          refKeyboardArrowDirection.current = 'up';
          setValueActive(getValueActive('up'));
          return false;
        }
        break;
      case 'Escape':
        if (popupVisible) {
          tryUpdatePopupVisible(false);
        }
        break;
    }
    return event;
  };
  const handleTokenSeparators = str => {
    let hasSeparator = false;
    if (isMultipleMode && isArray(tokenSeparators) && tokenSeparators.length) {
      const rawValues = str.split(new RegExp(`[${tokenSeparators.join('')}]`));
      if (rawValues.length > 1) {
        const splitValues = rawValues.filter((v, index) => v && rawValues.indexOf(v) === index);
        const newValue = value.slice(0);
        let needUpdate = false;
        splitValues.forEach(v => {
          if (newValue.indexOf(v) === -1) {
            newValue.push(v);
            needUpdate = true;
          }
        });
        if (needUpdate) {
          tryUpdateSelectValue(newValue);
        }
        hasSeparator = true;
      }
    }
    return hasSeparator;
  };

  // SelectView
  const selectViewEventHandlers = {
    onFocus,
    onBlur: event => {
      onBlur && onBlur(event);
      popupVisible && tryUpdateInputValue('', 'optionListHide');
    },
    onKeyDown: event => {
      if (event.target.tagName === 'INPUT' && event.target.value) {
        const isTab = event.key === Tab.key;
        const isEnter = event.key === Enter.key;
        const maybeTab = isTab ? '\t' : '';
        if (isEnter || isTab) {
          const suffix = isEnter ? '\n' : maybeTab;
          if (handleTokenSeparators(event.target.value + suffix)) {
            refTSLastSeparateTriggered.current = Date.now();
            tryUpdateInputValue('', 'tokenSeparator');
          }
        }
      }
      hotkeyHandler(event);
    },
    onChangeInputValue: (value, {
      nativeEvent: {
        inputType
      }
    }) => {
      if (inputType === 'insertFromPaste' && Date.now() - refTSLastSeparateTriggered.current < THRESHOLD_TOKEN_SEPARATOR_TRIGGER || handleTokenSeparators(value)) {
        tryUpdateInputValue('', 'tokenSeparator');
      } else {
        tryUpdateInputValue(value, 'manual');
      }
      if (!popupVisible && value) {
        tryUpdatePopupVisible(true);
      }
    },
    // Option Items
    onRemoveCheckedItem: (_, index, event) => {
      event.stopPropagation();
      uncheckOption(value[index]);
    },
    onClear: event => {
      event.stopPropagation();
      if (isMultipleMode) {
        const newValue = value.filter(v => {
          const item = optionInfoMap.get(v);
          return item && item.disabled;
        });
        tryUpdateSelectValue(newValue);
      } else {
        tryUpdateSelectValue(undefined);
      }
      tryUpdateInputValue('', 'manual');
      onClear && onClear(popupVisible);
    }
  };
  return {
    isMultipleMode,
    stateValue,
    setValue,
    value,
    inputValue,
    setInputValue,
    stateInputValue,
    setPopupVisible,
    popupVisible,
    valueActive,
    setValueActive,
    isNoOptionSelected,
    valueActiveDefault,
    scrollIntoView,
    childrenList,
    optionInfoMap,
    optionValueList,
    optionIndexListForArrowKey,
    hasOptGroup,
    refWrapper,
    refTrigger,
    refSelectView,
    refValueMap,
    refKeyboardArrowDirection,
    refOnInputChangeCallbackValue,
    refOnInputChangeCallbackReason,
    refTSLastSeparateTriggered,
    tryUpdateInputValue,
    tryUpdatePopupVisible,
    getOptionInfoByValue,
    getValueActive,
    getValueAndOptionForCallback,
    checkOption,
    uncheckOption,
    handleOptionClick,
    hotkeyHandler,
    selectViewEventHandlers,
    handleTokenSeparators
  };
};

export { useSelect };
